import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as ps from '../services/pocketsmith.service';
import { tw, monoFont, groupColors, getCategoryStyle } from '../utils/terminalTheme';

const VALID_GROUPS = ['PERSONAL', 'BUSINESS', 'INVESTMENT', 'OTHER'];

const Sparkline: React.FC<{ points: { balance: number }[]; width?: number; height?: number; color?: string }> = ({
  points,
  width = 100,
  height = 28,
  color,
}) => {
  if (points.length < 2) return null;
  const vals = points.map(p => p.balance);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const lineColor = color || (vals[vals.length - 1] >= vals[0] ? '#34d399' : '#ef4444');

  const pathPoints = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  });
  const d = 'M' + pathPoints.join(' L');

  return (
    <svg width={width} height={height} className="block">
      <path d={d} fill="none" stroke={lineColor} strokeWidth="1.5" />
    </svg>
  );
};

export const PocketSmithAccounts: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ps.GroupedAccountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['PERSONAL', 'BUSINESS', 'INVESTMENT', 'OTHER']));
  const [activeTab, setActiveTab] = useState('');
  const [balanceHistories, setBalanceHistories] = useState<Map<number, ps.BalanceHistory>>(new Map());

  const loadData = async () => {
    try {
      const result = await ps.getGroupedAccounts();
      setData(result);
    } catch (err) {
      console.error('Failed to load grouped accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (!data) return;
    const loadHistories = async () => {
      const allAccounts = data.groups.flatMap(g => g.accounts);
      const histMap = new Map<number, ps.BalanceHistory>();
      await Promise.allSettled(
        allAccounts.map(async (acct) => {
          try {
            const hist = await ps.getBalanceHistory(acct.id, 90);
            if (hist.data_points.length >= 2) {
              histMap.set(acct.id, hist);
            }
          } catch {}
        })
      );
      setBalanceHistories(histMap);
    };
    loadHistories();
  }, [data]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await ps.getAccounts();
      await loadData();
      toast.success('Accounts refreshed from PocketSmith');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const handleGroupChange = async (accountId: number, newGroup: string) => {
    try {
      await ps.updateAccountGroup(accountId, newGroup);
      toast.success('Group updated');
      await loadData();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to update group');
    }
  };

  const toggleGroup = (key: string) => {
    const next = new Set(expandedGroups);
    if (next.has(key)) next.delete(key); else next.add(key);
    setExpandedGroups(next);
  };

  const goToLedger = (accountName: string) => {
    navigate(`/pocketsmith?account=${encodeURIComponent(accountName)}`);
  };

  const goToGroupTransactions = (groupKey: string) => {
    navigate(`/pocketsmith?group=${groupKey}`);
  };

  if (loading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  const groups = data?.groups || [];
  const filteredGroups = activeTab ? groups.filter(g => g.key === activeTab) : groups;

  return (
    <div className={tw.pageWide}>
      {/* Terminal Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
            <span className="text-cyan-400">~/</span>pocketsmith/<span className="text-cyan-400">accounts</span>
            <span className="text-gray-600 text-sm font-normal ml-3">
              <span className="text-green-500">$</span> list --grouped
            </span>
          </h1>
          <p className="text-gray-500 text-sm font-mono mt-1">
            {groups.reduce((sum, g) => sum + g.account_count, 0)} accounts across {groups.length} groups
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className={`${tw.btnPrimary} ${refreshing ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {refreshing ? 'Refreshing...' : 'Refresh from PocketSmith'}
        </button>
      </div>

      {/* Group Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setActiveTab('')}
          className={activeTab === '' ? tw.filterActive : tw.filterInactive}
        >
          All
        </button>
        {groups.map(g => {
          const style = getCategoryStyle(g.key, groupColors);
          return (
            <button
              key={g.key}
              onClick={() => setActiveTab(activeTab === g.key ? '' : g.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all ${
                activeTab === g.key
                  ? `${style.bg} ${style.text} border ${style.border}`
                  : tw.filterInactive
              }`}
            >
              {g.label} ({g.account_count})
            </button>
          );
        })}
      </div>

      {/* Grand Total */}
      {data && (
        <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg px-5 py-4 mb-6 flex justify-between items-center">
          <span className={tw.statLabel + ' mb-0'}>Grand Total</span>
          <span className={`text-xl font-bold font-mono ${data.grand_total >= 0 ? 'text-green-500' : 'text-red-400'}`}>
            ${data.grand_total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      )}

      {/* Group Sections */}
      {filteredGroups.map(group => (
        <div key={group.key} className={tw.tableWrap + ' mb-4'}>
          {/* Group Header */}
          <div
            onClick={() => toggleGroup(group.key)}
            className="px-5 py-4 flex justify-between items-center cursor-pointer select-none hover:bg-gray-700/20 transition-colors"
            style={{ borderLeft: `3px solid ${group.color}` }}
          >
            <div className="flex items-center gap-3">
              <span className="text-gray-600 text-[10px] font-mono">{expandedGroups.has(group.key) ? '▼' : '▶'}</span>
              <span className="text-lg font-bold font-mono" style={{ color: group.color }}>{group.label}</span>
              <span
                className="px-2.5 py-0.5 rounded-full text-xs font-mono font-semibold"
                style={{ backgroundColor: group.color + '22', color: group.color }}
              >
                {group.account_count} accounts
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); goToGroupTransactions(group.key); }}
                className={tw.btnGhost + ' border border-gray-700/50 px-2.5 py-0.5 rounded'}
              >
                View Transactions
              </button>
            </div>
            <span className={`text-lg font-bold font-mono ${group.subtotal >= 0 ? 'text-green-500' : 'text-red-400'}`}>
              ${group.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* Account Rows */}
          {expandedGroups.has(group.key) && (
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700/30 bg-gray-900/30">
                  <th className={tw.tableHeader}>Account</th>
                  <th className={tw.tableHeader}>Institution</th>
                  <th className={tw.tableHeader}>Type</th>
                  <th className={tw.tableHeaderRight}>Balance</th>
                  <th className="px-5 py-3 text-center text-xs font-mono text-gray-500 uppercase tracking-wider">Trend (90d)</th>
                  <th className="px-5 py-3 text-center text-xs font-mono text-gray-500 uppercase tracking-wider">Group</th>
                </tr>
              </thead>
              <tbody className={tw.tableDivider}>
                {group.accounts.map(acct => (
                  <tr key={acct.id} className={tw.tableRow}>
                    <td className={tw.tableCell}>
                      <span
                        onClick={() => goToLedger(acct.name)}
                        className="text-cyan-400 hover:text-cyan-300 cursor-pointer text-sm font-mono font-medium transition-colors hover:underline"
                      >
                        {acct.name}
                      </span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-gray-400 text-xs font-mono">{acct.institution_name}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="bg-gray-700/50 text-gray-400 px-2 py-0.5 rounded text-[10px] font-mono">
                        {acct.account_type}
                      </span>
                    </td>
                    <td className={tw.tableCellRight}>
                      <span className={`text-sm font-bold font-mono ${acct.balance >= 0 ? 'text-green-500' : 'text-red-400'}`}>
                        ${acct.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      {balanceHistories.has(acct.id) ? (
                        <div className="flex flex-col items-center gap-0.5">
                          <Sparkline points={balanceHistories.get(acct.id)!.data_points} />
                          <span className={`text-[10px] font-mono ${balanceHistories.get(acct.id)!.change >= 0 ? 'text-green-500' : 'text-red-400'}`}>
                            {balanceHistories.get(acct.id)!.change >= 0 ? '+' : ''}
                            {balanceHistories.get(acct.id)!.change_pct.toFixed(1)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-700 text-[10px] font-mono">--</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <select
                        value={acct.account_group}
                        onChange={e => handleGroupChange(acct.id, e.target.value)}
                        className="bg-gray-900 text-gray-400 border border-gray-700 rounded px-2 py-1 text-[11px] font-mono focus:border-cyan-500 focus:outline-none"
                      >
                        {VALID_GROUPS.map(g => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
};

export default PocketSmithAccounts;
